// CareLogix™ ORACLE — ORACLE FUNDING COMMAND™ page (Phase 33 §4)
// Dedicated route /oracle/funding. Public preview + tier-gated depth.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PALETTE } from "@/components/animation/MotionShared";
import FundingOpportunityGraph     from "@/components/funding/FundingOpportunityGraph";
import FundingApplicationAssistant from "@/components/funding/FundingApplicationAssistant";
import FundingHeatmap              from "@/components/animated/FundingHeatmap";
import GrantDiscoveryFeed          from "@/components/animated/GrantDiscoveryFeed";
import ComplianceLock               from "@/components/compliance/ComplianceLock";
import { useMembership }            from "@/components/membership/MembershipContext";

const BUCKETS = [
  { v: "all",     l: "All Sources" },
  { v: "federal", l: "Federal"     },
  { v: "private", l: "Foundation / Corporate" },
  { v: "africa",  l: "Africa"      },
];

export default function OracleFundingCommand() {
  const { tier, tierLabel } = useMembership();
  const [bucket, setBucket] = useState("all");
  const showAssistant = tier !== "free";

  return (
    <main style={S.shell} data-testid="oracle-funding-command">
      <div style={S.bgGlow} aria-hidden="true" />
      <header style={S.header}>
        <div style={S.headerLeft}>
          <Link to="/" style={S.backLink} data-testid="funding-back">← ORACLE</Link>
          <div>
            <div style={S.eyebrow}>PHASE 33 · FUNDING INTELLIGENCE</div>
            <h1 style={S.title} data-testid="funding-title">ORACLE FUNDING COMMAND™</h1>
            <p style={S.subtitle}>Federal · State · Foundation · Corporate · Africa · Global — NiSi™ scored, decision-support only.</p>
          </div>
        </div>
        <div style={S.tierRow}>
          <span style={S.tierPill}>TIER: {tierLabel || tier}</span>
          {tier === "free" && <Link to="/access" style={S.upgrade}>Upgrade for application assistant →</Link>}
        </div>
      </header>

      <section style={S.bucketRow} data-testid="funding-buckets">
        {BUCKETS.map((b) => (
          <button key={b.v} type="button" onClick={() => setBucket(b.v)} style={{ ...S.bucket, ...(bucket === b.v ? S.bucketActive : null) }} data-testid={`funding-bucket-${b.v}`}>{b.l}</button>
        ))}
      </section>

      <FundingHeatmap bucket={bucket} testid="funding-heatmap" />
      <FundingOpportunityGraph bucket={bucket} limit={24} testid="funding-opp-graph" />
      <GrantDiscoveryFeed testid="funding-grant-discovery-feed" />

      {showAssistant ? (
        <FundingApplicationAssistant testid="funding-application-assistant" />
      ) : (
        <section style={S.upgradeBlock} data-testid="funding-upgrade-block">
          <h3 style={S.upTitle}>Funding Application Assistant™</h3>
          <p style={S.upBody}>Upgrade to Pro, Executive, or Enterprise to unlock the LOGIX-2 application assistant: eligibility snapshot, executive summary, document checklists, and HITL pursuit recommendation.</p>
          <Link to="/access" style={S.upBtn} data-testid="funding-upgrade-cta">Upgrade →</Link>
        </section>
      )}

      <ComplianceLock variant="footer" testid="funding-compliance-lock" />
    </main>
  );
}

const S = {
  shell:    { position: "relative", minHeight: "100vh", padding: "20px clamp(12px, 3vw, 28px) 60px", background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.45) 0%, rgba(4,6,8,1) 70%)", color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  bgGlow:   { position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse 500px 280px at 50% 0%, rgba(197,160,89,0.10), transparent 65%)", pointerEvents: "none" },
  header:   { position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  headerLeft:{ display: "flex", gap: 16, alignItems: "flex-start" },
  backLink: { color: PALETTE.gold, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 12, padding: "6px 12px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 999 },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.24em" },
  title:    { margin: "4px 0 0", color: PALETTE.goldBright, fontSize: "clamp(20px, 3.2vw, 28px)", letterSpacing: "0.08em", fontWeight: 800 },
  subtitle: { margin: "4px 0 0", color: PALETTE.slate4, fontSize: 13 },
  tierRow:  { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 },
  tierPill: { fontFamily: "'Space Mono', monospace", color: PALETTE.goldBright, fontSize: 11, padding: "4px 10px", border: `1px solid ${PALETTE.gold}`, borderRadius: 999 },
  upgrade:  { color: PALETTE.gold, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  bucketRow:{ display: "flex", flexWrap: "wrap", gap: 6 },
  bucket:   { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  bucketActive:{ background: PALETTE.gold, color: PALETTE.bgDeep, borderColor: PALETTE.gold, fontWeight: 800 },
  upgradeBlock:{ background: "rgba(252,211,77,0.06)", border: `1px solid ${PALETTE.amber}`, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 6 },
  upTitle:  { margin: 0, color: PALETTE.amber, fontSize: 16, letterSpacing: "0.08em" },
  upBody:   { margin: 0, color: PALETTE.slate3, fontSize: 13 },
  upBtn:    { alignSelf: "flex-start", marginTop: 4, color: PALETTE.bgDeep, background: PALETTE.amber, padding: "6px 14px", borderRadius: 999, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontWeight: 800, fontSize: 12 },
};
